/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react/require-default-props */
/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import useYupValidationResolver from 'src/hooks/useYupValidationResolver';
import * as yup from 'yup';
import { twMerge } from 'tailwind-merge';
import { MdInsertDriveFile } from 'react-icons/md';
import RHFInput from 'src/components/elements/Input/RHFInput';
import { CircularProgress, InputLabel, TextField } from '@mui/material';
import { confirmWrapper } from 'src/components/Dialog';
import FileIconCareers from 'src/icons/FileIconCareers';
import { ResumeData } from '../types';
import Button from './Button';

const schema = yup.object().shape({
  firstName: yup.string().required('Please enter the first name.')
    .matches(/^[A-Za-z]+(?: [A-Za-z]+)*$/, 'Enter a valid first name.'),
  lastName: yup.string().required('Please enter the last name.')
    .matches(/^[A-Za-z]+(?: [A-Za-z]+)*$/, 'Enter a valid last name.'),
  phoneNumber: yup.string().required('Enter the phone number')
    .matches(/^(?:\+\d{1,2}\s?)?\d{10}$/, 'Enter valid phone number'),
  emailAddress: yup.string().email('Enter valid email id.').required('Please enter the email id.'),
  // .test('Email', 'Temporary emails are not allowed.', excludeTempEmails),
});

interface FieldsValues {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  emailAddress: string;
}

interface UploadResumeProps {
  loading: boolean;
  organizationId: number;
  resumeName: string;
  setResumeName: (name: string) => void;
  onUploadResume: (e: React.ChangeEvent<HTMLInputElement>) => Promise<{ response: ResumeData | null, error: string }>;
  onApplyJob: (formValues: any, resumeData: ResumeData) => void;
}

function UploadResume({
  onApplyJob, onUploadResume, resumeName, setResumeName, loading, organizationId,
}: UploadResumeProps) {
  const [resumeProcessing, setResumeProcessing] = useState(false);
  const [resumeData, setResumeData] = useState<ResumeData>();
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FieldsValues>({
    resolver: useYupValidationResolver(schema),
  });

  const uploadResume = async (e: any) => {
    try {
      setResumeProcessing(true);
      const { response, error } = await onUploadResume(e);
      if (error !== '') {
        throw new Error(error);
      }
      if (response) {
        setResumeData(response);
      }
    } catch (error) {
      await confirmWrapper(
        'Error uploading resume',
        {
          variant: 'danger',
          hidePrimaryBtn: true,
          hideSecondaryBtn: true,
          maxWidth: 'sm',
          fullWidth: true,
          autoClose: true,
        },
      );
    } finally {
      setResumeProcessing(false);
    }
  };

  const onRemoveResume = () => {
    setResumeProcessing(true);
    setResumeData(undefined);
    setResumeName('');
    setResumeProcessing(false);
  };

  const onSubmit = (data: FieldsValues) => {
    onApplyJob(data, resumeData as ResumeData);
  };

  useEffect(() => {
    setValue('firstName', resumeData?.firstName || '');
    setValue('lastName', resumeData?.lastName || '');
    setValue('emailAddress', resumeData?.email || '');
    setValue('phoneNumber', resumeData?.phoneNumber || '');
  }, [resumeData]);

  return (
    <div className="mx-auto w-full max-w-[660px]">
      <h2 className="font-bold py-0 my-0 text-center text-2xl">Apply Now</h2>
      <section className="mb-3">
        <h2 className="mb-2 text-lg">Upload your resume</h2>
        <div className="relative border-[#2065EC] border-2 p-3 border-dashed rounded-lg text-center">
          {resumeProcessing && (
            <div className="absolute inset-0 flex justify-center items-center bg-white bg-opacity-75 z-10">
              <CircularProgress className="text-[#00349A]" />
            </div>
          )}
          {resumeName && resumeName !== '' ? (
            <div className="flex flex-col items-center gap-2">
              <div className="relative flex justify-center items-center bg-[#2065EC1A] rounded w-[182px] h-[131px]">
                <div className="flex justify-center items-center gap-1 bg-white shadow-md px-2 rounded-lg w-fit max-w-[298px] h-10">
                  <FileIconCareers />
                  <span className="text-sm w-[298px] truncate">
                    {resumeName}
                  </span>
                </div>
              </div>
              <Button
                variant="outlined"
                className=""
                onClick={onRemoveResume}
              >
                Remove
              </Button>
            </div>
          ) : (
            <>
              <img src="/UploadResume.svg" alt="Upload Resume" width="225" height="131" />
              <h2 className="text-lg">
                Drag and drop your resume here
              </h2>
              <Button
                variant="outlined"
                className="relative"
                component="label"
              >
                Select files
                <input
                  hidden
                  disabled={resumeProcessing}
                  type="file"
                  id="uploadResumeWeb"
                  accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={uploadResume}
                />
              </Button>
            </>
          )}
          <p className="mt-2 text-gray-500 text-sm">
            (Doc, Docx, Text, Rtf or Pdf - size less than 10mb)
          </p>
        </div>
      </section>
      <section className="mt-2">
        <form onSubmit={handleSubmit(onSubmit)}>
          <h3 className="mt-4 mb-2 text-xl">Enter your details</h3>
          <div className="gap-4 border-[#D9D9DB] grid grid-cols-1 sm:grid-cols-2 p-3 border border-solid rounded-lg">
            <Controller
              name="firstName"
              disabled={resumeProcessing || resumeData === undefined}
              control={control}
              render={({ field }) => (
                <div className="flex flex-col justify-center items-start gap-1">
                  <InputLabel aria-controls="firstName" className="font-bold text-[#28364C] fontSize-[1rem]" htmlFor="firstName">First Name</InputLabel>
                  <TextField
                    {...field}
                    placeholder="First Name"
                    className="hover:border-none hover:outline-none hover:ring-0"
                    id="outlined-basic"
                    color="secondary"
                    defaultValue={field.value}
                    value={field.value}
                    type="text"
                    variant="outlined"
                    fullWidth
                    error={!!errors.firstName}
                    helperText={errors.firstName?.message}
                  />
                </div>
              )}
            />
            <Controller
              name="lastName"
              disabled={resumeProcessing || resumeData === undefined}
              control={control}
              render={({ field }) => (
                <div className="flex flex-col justify-center items-start gap-1">
                  <InputLabel aria-controls="lastName" className="font-bold text-[#28364C] fontSize-[1rem]" htmlFor="lastName">Last Name</InputLabel>
                  <TextField
                    {...field}
                    placeholder="Last Name"
                    className="hover:border-none hover:outline-none hover:ring-0"
                    id="outlined-basic"
                    color="secondary"
                    defaultValue={field.value}
                    value={field.value}
                    type="text"
                    variant="outlined"
                    fullWidth
                    error={!!errors.lastName}
                    helperText={errors.lastName?.message}
                  />
                </div>
              )}
            />

            <Controller
              name="emailAddress"
              disabled={resumeProcessing || resumeData === undefined}
              control={control}
              render={({ field }) => (
                <div className="flex flex-col justify-center items-start gap-1">
                  <InputLabel aria-controls="emailAddress" className="font-bold text-[#28364C] fontSize-[1rem]" htmlFor="emailAddress">Email Address</InputLabel>
                  <TextField
                    {...field}
                    placeholder="Email Address"
                    className="hover:border-none hover:outline-none hover:ring-0"
                    id="outlined-basic"
                    color="secondary"
                    defaultValue={field.value}
                    value={field.value}
                    type="text"
                    variant="outlined"
                    fullWidth
                    error={!!errors.emailAddress}
                    helperText={errors.emailAddress?.message}
                  />
                </div>
              )}
            />

            <Controller
              name="phoneNumber"
              disabled={resumeProcessing || resumeData === undefined}
              control={control}
              render={({ field }) => (
                <div className="flex flex-col justify-center items-start gap-1">
                  <InputLabel aria-controls="phoneNumber" className="font-bold text-[#28364C] fontSize-[1rem]" htmlFor="phoneNumber">Phone Number</InputLabel>
                  <TextField
                    {...field}
                    placeholder="Phone Number"
                    className="hover:border-none hover:outline-none hover:ring-0"
                    id="outlined-basic"
                    color="secondary"
                    defaultValue={field.value}
                    value={field.value}
                    type="text"
                    variant="outlined"
                    fullWidth
                    error={!!errors.phoneNumber}
                    helperText={errors.phoneNumber?.message}
                  />
                </div>
              )}
            />

          </div>
          <div className="col-span-2 mt-6 text-center">
            <Button
              variant="contained"
              type="submit"
              className="bg-[#2065EC] hover:bg-blue-700 hover:bg-[#2065EC]/80 py-2 rounded w-fit text-white"
            >
              {loading ? 'Applying...' : 'Continue'}
            </Button>
          </div>
        </form>
        <p className="mt-4 text-center text-secondary text-xs">
          By clicking on continue, you will agree with
          {' '}
          <a href={`/${organizationId}/job-terms`} target="_blank" className="text-blue-600 underline" rel="noreferrer">terms and conditions</a>
        </p>
      </section>
    </div>
  );
}

export default UploadResume;
